import i18n, { Module } from 'i18next'

import {
  AppLocale,
  DEFAULT_LOCALE,
  getAvailableLocales,
  isAppLocale,
  Locale,
  PSEUDO_LOCALE
} from './constants'
import { getDeviceLanguageTags } from './deviceLanguages'
import { pseudoLocalizeResources } from './pseudoLocalize'
import { pickDeviceLocale } from './resolveLocale'
import en from './translations/en.json'

// React-free on purpose: the extension background service worker initialises
// i18n through this file too, and must not pull in react-i18next.

export const I18N_NAMESPACE = 'app'

type Translations = Record<string, string>
export type TranslationsLoader = (locale: Exclude<Locale, Locale.EN>) => Promise<Translations>

let loadTranslations: TranslationsLoader | null = null
const loadedLocales = new Set<string>([DEFAULT_LOCALE])
const pendingLoads = new Map<string, Promise<void>>()

export const initI18nCore = ({
  loader,
  plugins = []
}: {
  loader: TranslationsLoader
  plugins?: Module[]
}) => {
  loadTranslations = loader
  if (i18n.isInitialized) return i18n

  plugins.forEach((plugin) => i18n.use(plugin))
  void i18n.init({
    // Always start in English (bundled), then `applyLocale` switches once the
    // selected locale's file is loaded.
    lng: DEFAULT_LOCALE,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...Object.values(Locale), PSEUDO_LOCALE],
    // Only the exact code: never look up `zh` for `zh-CN`, or `en` twice for `en-XA`.
    load: 'currentOnly',
    ns: [I18N_NAMESPACE],
    defaultNS: I18N_NAMESPACE,
    resources: { [DEFAULT_LOCALE]: { [I18N_NAMESPACE]: en } },
    partialBundledLanguages: true,
    initAsync: false,
    // Keys are the English sentences themselves and contain `.` and `:`.
    keySeparator: false,
    nsSeparator: false,
    // A key the translate script could not translate is stored as "" and must
    // fall back to English instead of rendering nothing.
    returnEmptyString: false,
    interpolation: { escapeValue: false },
    react: { useSuspense: false }
  })

  return i18n
}

export const ensureLocaleLoaded = (locale: AppLocale): Promise<void> => {
  if (loadedLocales.has(locale)) return Promise.resolve()
  const pending = pendingLoads.get(locale)
  if (pending) return pending

  const load = (async () => {
    let resources: Translations
    if (locale === PSEUDO_LOCALE) {
      resources = pseudoLocalizeResources(en)
    } else {
      if (!loadTranslations) throw new Error('i18n: initI18nCore was not called')
      resources = await loadTranslations(locale as Exclude<Locale, Locale.EN>)
    }
    i18n.addResourceBundle(locale, I18N_NAMESPACE, resources, true, true)
    loadedLocales.add(locale)
  })().finally(() => pendingLoads.delete(locale))

  pendingLoads.set(locale, load)
  return load
}

/**
 * Loads the locale's translations, then switches to it. When loading fails the
 * language still switches: every key falls back to English, which beats being
 * stuck on the previous language.
 */
export const applyLocale = async (locale: AppLocale) => {
  try {
    await ensureLocaleLoaded(locale)
  } catch (error) {
    console.warn(`i18n: failed to load "${locale}" translations`, error)
  }
  if (i18n.language !== locale) await i18n.changeLanguage(locale)
}

/**
 * The stored preference if it is a locale this build offers; otherwise ("system",
 * nothing stored, or a locale that is no longer released) the device language,
 * falling back to English.
 */
export const resolveLocalePreference = (preference: unknown): AppLocale => {
  const available = getAvailableLocales()
  if (isAppLocale(preference) && available.includes(preference)) return preference
  return pickDeviceLocale(getDeviceLanguageTags(), available) ?? DEFAULT_LOCALE
}

export default i18n
